// Voicemail drop libraries (round 13, owner requirement 2026-10-22): pure types, response readers, ordering and copy.
// A library is keyed to the line: each workspace number has its own drops with one default, and each person's
// direct line has several with one default. On an outbound call the picker offers the calling number's drops,
// then the agent's own (briefs/B11.md "Design (pinned)"). Nothing here invents a drop: every row comes from the
// server and is re-validated, and a row this version can't read is counted, never guessed at.
import type { Session } from '@bcio-voice/contracts';
import type { VoicemailDropOwner, VoicemailDropScope } from '../../lib/api.ts';
import { sessionRoles } from '../routing/access.ts';
import { type Result, type VoicemailFault, toFault as voicemailFault } from './model.ts';

export const DROP_SCOPES = ['number', 'user_line'] as const satisfies readonly VoicemailDropScope[];

/** Library limits: 10 per number and 10 per direct line unless the server says otherwise; the WAV rules match greetings. */
export const DROP_LIMITS = Object.freeze({ perLibrary: 10, nameMax: 48, minMs: 1000, maxMs: 60000, maxBytes: 1048576 });

export interface VoicemailDrop {
  id: string;
  scope: VoicemailDropScope;
  number_id: string | null;
  user_id: string | null;
  name: string;
  duration_ms: number;
  sort_order: number;
  is_default: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** Sent back on changes when the server versions drops; null when it doesn't. */
  version: number | null;
}

/** One choice in a call's picker. */
export interface DropOption {
  id: string;
  name: string;
  duration_ms: number;
  scope: VoicemailDropScope;
  is_default: boolean;
  sort_order: number | null;
}

export interface DropOptions {
  options: DropOption[];
  /** The server's one-tap choice for this call, when it names one. */
  defaultId: string | null;
  /** The server's answer when the options were read (null when it didn't say). */
  eligible: boolean | null;
  /** Why not, as the server's code (`voicemail_drop_monitored`, ...). */
  reasonCode: string | null;
  skipped: number;
}

// ---- Readers ---------------------------------------------------------------------------------------------

const ID = /^[A-Za-z0-9_.:-]{1,128}$/;
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
const id = (value: unknown): string | null => (typeof value === 'string' && ID.test(value) ? value : null);
const int = (value: unknown, min: number, max = Number.MAX_SAFE_INTEGER): number | null => (typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max ? value : null);
const time = (value: unknown): string | null => (typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null);
const scopeOf = (value: unknown): VoicemailDropScope | null => (value === 'number' || value === 'user_line' ? value : null);

/** A drop's name as shown: control characters and runs of spaces collapsed, at most the name limit. */
export function cleanName(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
  return text ? text.slice(0, 100) : null;
}

export function readDrop(value: unknown): VoicemailDrop | null {
  if (!isRecord(value)) return null;
  const dropId = id(value.id), scope = scopeOf(value.scope), name = cleanName(value.name), duration = int(value.duration_ms, 0, 600_000);
  if (!dropId || !scope || !name || duration === null || typeof value.is_default !== 'boolean') return null;
  const numberId = id(value.number_id), userId = id(value.user_id);
  if (scope === 'number' ? !numberId : !userId) return null;
  return {
    id: dropId, scope, number_id: numberId, user_id: userId, name, duration_ms: duration,
    sort_order: int(value.sort_order, 0, 10_000) ?? 0, is_default: value.is_default,
    created_by: id(value.created_by), created_at: time(value.created_at), updated_at: time(value.updated_at), version: int(value.version, 1),
  };
}

/** The list body: `{data: [...]}` (the collection envelope), `{items: [...]}` or a bare array. */
function rowsOf(body: unknown): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (!isRecord(body)) return null;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.items)) return body.items;
  if (isRecord(body.data) && Array.isArray(body.data.items)) return body.data.items;
  return null;
}

/** The library limit the server reports (`meta.limit`, `limit` or `data.limit`), else the pinned 10. */
export function limitOf(body: unknown): number {
  const r = isRecord(body) ? body : {};
  const candidates = [isRecord(r.meta) ? r.meta.limit : undefined, r.limit, isRecord(r.data) ? r.data.limit : undefined];
  for (const c of candidates) {
    const n = int(c, 1, 100);
    if (n !== null) return n;
  }
  return DROP_LIMITS.perLibrary;
}

/** One library in its sort order, with the rows this version couldn't read counted. */
export function readLibrary(body: unknown): { drops: VoicemailDrop[]; skipped: number; limit: number } | null {
  const rows = rowsOf(body);
  if (!rows) return null;
  const drops: VoicemailDrop[] = [];
  let skipped = 0;
  for (const row of rows) {
    const drop = readDrop(row);
    if (drop) drops.push(drop);
    else skipped += 1;
  }
  return { drops: sortLibrary(drops), skipped, limit: limitOf(body) };
}

type Ordered = { is_default: boolean; sort_order: number | null; name: string; id: string };

/** The server's library order (store.ts list): the default first, then sort order, then name, then id. */
function libraryCompare(a: Ordered, b: Ordered): number {
  return Number(b.is_default) - Number(a.is_default)
    || (a.sort_order ?? Number.MAX_SAFE_INTEGER) - (b.sort_order ?? Number.MAX_SAFE_INTEGER)
    || a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/** A library as agents see it: the default first, then the rest in their order. */
export function sortLibrary(drops: readonly VoicemailDrop[]): VoicemailDrop[] {
  return [...drops].sort(libraryCompare);
}

/** A changed drop answered as `{data: drop}` or the bare drop. */
export function readOne(body: unknown): VoicemailDrop | null {
  return readDrop(isRecord(body) && isRecord(body.data) ? body.data : body);
}

export function readOption(value: unknown): DropOption | null {
  if (!isRecord(value)) return null;
  const optionId = id(value.drop_id) ?? id(value.id), scope = scopeOf(value.scope), name = cleanName(value.name), duration = int(value.duration_ms, 0, 600_000);
  if (!optionId || !scope || !name || duration === null) return null;
  return { id: optionId, name, duration_ms: duration, scope, is_default: value.is_default === true, sort_order: int(value.sort_order, 0, 10_000) };
}

/**
 * GET /calls/{id}/voicemail-drop-options: `{data: [...]}`, `{data: {options, default_drop_id}}` or a bare list.
 * The result is always in picker order (pickerOrder), whatever order the rows came in.
 */
export function readOptions(body: unknown): DropOptions | null {
  const inner = isRecord(body) && isRecord(body.data) ? body.data : isRecord(body) && !Array.isArray(body.data) ? body : null;
  const rows = inner && Array.isArray(inner.options) ? inner.options : rowsOf(body);
  if (!rows) return null;
  const options: DropOption[] = [];
  let skipped = 0;
  for (const row of rows) {
    const option = readOption(row);
    if (option && !options.some(o => o.id === option.id)) options.push(option);
    else skipped += 1;
  }
  const named = inner ? id(inner.default_drop_id) : null;
  const eligible = inner && typeof inner.eligible === 'boolean' ? inner.eligible : null;
  const reasonCode = inner && typeof inner.reason_code === 'string' && /^[a-z0-9_]{1,100}$/.test(inner.reason_code) ? inner.reason_code : null;
  return { options: pickerOrder(options), defaultId: named && options.some(o => o.id === named) ? named : null, eligible, reasonCode, skipped };
}

// ---- The picker ------------------------------------------------------------------------------------------

/** The contract's order: this number's drops, then mine; each group in its library order (default first). */
export function pickerOrder(options: readonly DropOption[]): DropOption[] {
  const rank = (o: DropOption) => (o.scope === 'number' ? 0 : 1);
  return [...options].sort((a, b) => rank(a) - rank(b) || libraryCompare(a, b));
}

/**
 * The one-tap choice: the server's named default when it names one; else the calling number's default, then my
 * direct line's default; else the first drop in picker order. Null when there's nothing to drop.
 */
export function defaultOption(options: DropOptions | readonly DropOption[] | null): DropOption | null {
  if (!options) return null;
  const list = Array.isArray(options) ? pickerOrder(options as readonly DropOption[]) : (options as DropOptions).options;
  const named = Array.isArray(options) ? null : (options as DropOptions).defaultId;
  return (named ? list.find(o => o.id === named) : undefined)
    ?? list.find(o => o.scope === 'number' && o.is_default)
    ?? list.find(o => o.scope === 'user_line' && o.is_default)
    ?? list[0]
    ?? null;
}

export interface OptionGroup {
  scope: VoicemailDropScope;
  title: string;
  options: DropOption[];
}

/** The picker's two labelled groups, in order; empty groups are left out. */
export function optionGroups(options: readonly DropOption[], numberLabel: string | null): OptionGroup[] {
  const ordered = pickerOrder(options);
  const groups: OptionGroup[] = [
    { scope: 'number', title: numberLabel ? `This number: ${numberLabel}` : 'This number', options: ordered.filter(o => o.scope === 'number') },
    { scope: 'user_line', title: 'Your direct line', options: ordered.filter(o => o.scope === 'user_line') },
  ];
  return groups.filter(g => g.options.length > 0);
}

// ---- Library rules ---------------------------------------------------------------------------------------

/** Why a new name can't be used, before anything is sent; null when it can. Names are unique in a library, ignoring case. */
export function nameProblem(name: string, library: readonly VoicemailDrop[], exceptId: string | null = null): string | null {
  const clean = name.replace(/\s+/g, ' ').trim();
  if (!clean) return 'Give the drop a name, so agents can tell it apart.';
  if (clean.length > DROP_LIMITS.nameMax) return `Use at most ${DROP_LIMITS.nameMax} characters.`;
  if (library.some(d => d.id !== exceptId && d.name.toLowerCase() === clean.toLowerCase())) return 'Another drop in this library already has this name.';
  return null;
}

/**
 * The new 0-based place for a move up (-1) or down (+1); null at either end. The server always lists the default
 * first, so the default doesn't move and nothing moves above it: "Make default" is how a drop gets to the top.
 */
export function movedIndex(library: readonly VoicemailDrop[], dropId: string, delta: -1 | 1): number | null {
  const at = library.findIndex(d => d.id === dropId);
  if (at < 0 || library[at]!.is_default) return null;
  const floor = library[0]?.is_default ? 1 : 0;
  const to = at + delta;
  return to < floor || to >= library.length ? null : to;
}

/** The library after a move, numbered 0..n in its new order, for showing it at once while the server confirms. */
export function applyMove(library: readonly VoicemailDrop[], dropId: string, to: number): VoicemailDrop[] {
  const list = [...library];
  const at = list.findIndex(d => d.id === dropId);
  if (at < 0 || to < 0 || to >= list.length) return list;
  const [moved] = list.splice(at, 1);
  list.splice(to, 0, moved!);
  return list.map((d, i) => ({ ...d, sort_order: i }));
}

/**
 * The PATCHes a move needs: the server stores one sort order per drop (PATCH changes only the drop it names), so
 * every drop whose number changed is sent its new place with its own version. Usually two.
 */
export function reorderPatches(before: readonly VoicemailDrop[], after: readonly VoicemailDrop[]): { drop: VoicemailDrop; sort_order: number }[] {
  const was = new Map(before.map(d => [d.id, d]));
  return after.flatMap((d, i) => {
    const original = was.get(d.id);
    return original && original.sort_order !== i ? [{ drop: original, sort_order: i }] : [];
  });
}

export const ownerKey = (owner: VoicemailDropOwner) => (owner.scope === 'number' ? `number:${owner.numberId}` : `user_line:${owner.userId}`);

/** "3 of 10 drops" and, at the limit, why Add is gone. */
export function countLine(count: number, limit: number): string {
  return `${count} of ${limit} drop${limit === 1 ? '' : 's'}`;
}

// ---- Faults ----------------------------------------------------------------------------------------------

/** Plain copy for the drop routes' codes; the server's own message stays on the fault and is shown beside it. */
export const DROP_COPY: Readonly<Record<string, string>> = Object.freeze({
  permission_denied: 'Your role can’t change this library.',
  not_found: 'This drop was deleted, or it isn’t shared with you any more.',
  version_conflict: 'Someone else changed this library since you opened it. It was reloaded; try again.',
  voicemail_drop_limit: 'This library already has the most drops it can hold. Delete one to add another.',
  voicemail_drop_limit_reached: 'This library already has the most drops it can hold. Delete one to add another.',
  voicemail_drop_name_conflict: 'Another drop in this library already has this name.',
  voicemail_drop_name_invalid: 'Use a name of 1 to 48 characters.',
  invalid_input: 'The server didn’t accept these details. Check the name and try again.',
  voicemail_drop_schema_unavailable: 'Voicemail drops aren’t installed on the server yet.',
  voicemail_drops_schema_unavailable: 'Voicemail drops aren’t installed on the server yet.',
  greeting_invalid_wav: 'The server couldn’t read this file as a WAV recording.',
  greeting_format_unsupported: 'The server only accepts 8 kHz, 16-bit, mono WAV audio.',
  greeting_too_short: 'A voicemail drop must be at least 1 second long.',
  greeting_too_long: 'A voicemail drop can be at most 60 seconds long.',
  greeting_too_large: 'A voicemail drop file can be at most 1 MiB.',
  unsupported_media_type: 'The server only accepts voicemail drops sent as WAV audio.',
  greeting_storage_unavailable: 'The server couldn’t store the audio right now. Nothing was changed. Try again later.',
  invalid_response: 'The server sent voicemail drop data this version can’t read.',
});

export function dropFault(error: unknown, fallbackTitle: string): VoicemailFault {
  const fault = voicemailFault(error, fallbackTitle);
  const copy = !fault.outcomeUnknown ? DROP_COPY[fault.code] : undefined;
  return copy ? { ...fault, title: copy } : fault;
}

export const unreadableDrops = (): VoicemailFault => ({ title: DROP_COPY.invalid_response!, detail: '', code: 'invalid_response', requestId: null, retryable: true, status: 200, serverMessage: '', outcomeUnknown: false });

/**
 * For the collection read only: the server has no drop routes (an API before round 13 answers an unknown route
 * with 404 `not_found`, like release 14), or the routes are there but migration 023 isn't (503
 * `voicemail_drop_schema_unavailable`). B11 also answers 404 for a library whose line isn't visible to this person;
 * either way there is no library to show here, and calls aren't affected.
 */
export const isDropsMissing = (fault: VoicemailFault) =>
  (fault.status === 503 && (fault.code === 'voicemail_drop_schema_unavailable' || fault.code === 'voicemail_drops_schema_unavailable'))
  || fault.status === 404 || fault.status === 405 || fault.status === 501;

export type { Result, VoicemailFault };

// ---- Who manages which library -------------------------------------------------------------------------

/**
 * Mirrors apps/api/src/voicemail-drops/permissions.ts from the session's own roles: owners and admins (or
 * `voicemail_drops:manage_all`) manage every library, including each number's; agents and supervisors (or
 * `voicemail_drops:manage_own`) manage their own direct line's. The server stays the authority: its 403 turns
 * changes off in the panel.
 */
export function dropAccess(session: Pick<Session, 'tenant' | 'memberships' | 'permissions'>): { manageAll: boolean; manageOwn: boolean } {
  const roles = sessionRoles(session);
  const manageAll = roles.some(r => r === 'owner' || r === 'admin') || session.permissions.includes('voicemail_drops:manage_all');
  const manageOwn = manageAll || roles.some(r => r === 'agent' || r === 'supervisor') || session.permissions.includes('voicemail_drops:manage_own');
  return { manageAll, manageOwn };
}

// ---- Audio this tab already has ------------------------------------------------------------------------

/**
 * The WAV of each drop added from this tab, so its Listen button works at once and on servers without an audio
 * route. Memory only, for this page's life; never stored.
 */
const added = new Map<string, Uint8Array<ArrayBuffer>>();
export function rememberAudio(dropId: string, wav: Uint8Array<ArrayBuffer>): void {
  added.set(dropId, wav);
  if (added.size > 20) added.delete(added.keys().next().value!);
}
export function rememberedAudio(dropId: string): Uint8Array<ArrayBuffer> | null {
  return added.get(dropId) ?? null;
}

// ---- Copy ------------------------------------------------------------------------------------------------

export const DROP_FILE_HINT = '1 to 60 seconds; shorter is safer, because some answering machines stop recording early. A WAV file in the phone format (8 kHz, 16-bit, mono) is uploaded exactly as it is. Other audio this browser can play is converted to that format here first.';

/** What a library is for, under its title. */
export function libraryPurpose(owner: VoicemailDropScope): string {
  return owner === 'number'
    ? 'What agents can leave on an answering machine when they call from this number. The default is the one-tap choice in the phone panel; agents can pick any other drop from the list.'
    : 'Messages you can leave on an answering machine from any call you place. On a call, the calling number’s drops come first, then these. Your default is the one-tap choice when the number has none.';
}

export const DROP_LEGAL_NOTE = 'A drop plays to whoever answers. Keep it to what you would say on a voicemail yourself, and follow your workspace’s calling rules.';
